import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { getAnalytics, provideAnalytics } from '@angular/fire/analytics';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { connectAuthEmulator, getAuth, provideAuth } from '@angular/fire/auth';
import { AngularFireModule, FIREBASE_OPTIONS } from '@angular/fire/compat';
import {
  DEBUG_MODE as ANALYTICS_DEBUG_MODE,
  APP_NAME,
  APP_VERSION,
  AngularFireAnalyticsModule,
  COLLECTION_ENABLED,
  ScreenTrackingService,
  UserTrackingService,
} from '@angular/fire/compat/analytics';
import {
  AngularFireAuthModule,
  USE_EMULATOR as USE_AUTH_EMULATOR,
  USE_DEVICE_LANGUAGE,
} from '@angular/fire/compat/auth';
import { AngularFireAuthGuardModule } from '@angular/fire/compat/auth-guard';
import {
  AngularFirestoreModule,
  SETTINGS as FIRESTORE_SETTINGS,
  USE_EMULATOR as USE_FIRESTORE_EMULATOR,
} from '@angular/fire/compat/firestore';
import {
  AngularFireFunctionsModule,
  USE_EMULATOR as USE_FUNCTIONS_EMULATOR,
} from '@angular/fire/compat/functions';
import {
  AngularFireStorageModule,
  USE_EMULATOR as USE_STORAGE_EMULATOR,
} from '@angular/fire/compat/storage';
import { connectFirestoreEmulator, getFirestore, provideFirestore } from '@angular/fire/firestore';
import {
  FunctionsModule,
  connectFunctionsEmulator,
  getFunctions,
  provideFunctions,
} from '@angular/fire/functions';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { getPerformance, providePerformance } from '@angular/fire/performance';
import { connectStorageEmulator, getStorage, provideStorage } from '@angular/fire/storage';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { CodemirrorModule } from '@ctrl/ngx-codemirror';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgbModule, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { Bs5UnixCronModule } from '@sbzen/ng-cron';
import { LottieComponent } from 'ngx-lottie';
import { ToastrModule } from 'ngx-toastr';

// Swiper Angular module removed in v9+ - now using Swiper Element
import { environment } from '../environments/environment';

import { AgentFieldNoteComponent } from './components/agent-field-note/agent-field-note.component';
import { ActionContentDirective } from './components/list-view/action-content.directive';
import { ExpandContentDirective } from './components/list-view/expand-content.directive';
import { ItemActionsContentDirective } from './components/list-view/item-actions-content.directive';
import { ItemLeadingActionsContentDirective } from './components/list-view/item-leading-actions-content.directive';
import { ListViewComponent } from './components/list-view/list-view.component';
import { LoaderComponent } from './components/loader/loader.component';

import { PlatformApiModule } from './api/platform/platform-api.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { noHttpTransferCacheInterceptor } from './interceptors/no-http-transfer-cache.interceptor';
import { platformAuthInterceptor } from './interceptors/platform-auth.functional.interceptor';
import { AppLayoutComponent } from './layouts/app-layout/app-layout.component';
import { AuthLayoutComponent } from './layouts/auth-layout/auth-layout.component';
import { AddAdminUserModalComponent } from './modals/add-admin-user-modal/add-admin-user-modal.component';
import { AddProductsStrategySelectModalComponent } from './modals/add-products-modal/add-products-modal.component';
import { ConfirmModalComponent } from './modals/confirm-modal/confirm-modal.component';
import { CronExpressionEditModalComponent } from './modals/cron-expression-edit-modal/cron-expression-edit-modal.component';
import { DeleteUserModalComponent } from './modals/delete-user-modal/delete-user-modal.component';
import { EditStoreModalComponent } from './modals/edit-store-modal/edit-store-modal.component';
import { TypedConfirmModalComponent } from './modals/typed-confirm-modal/typed-confirm-modal.component';
import { AdminUsersComponent } from './pages/admin-users/admin-users.component';
import { CmsComponent } from './pages/cms';
import { LegalContentManagerComponent } from './pages/cms/legal-content-manager';
import { MediaFileManagerComponent } from './pages/cms/media-file-manager';
import { PlatformTextPropertiesFormComponent } from './pages/cms/platform-text-properties-form-component';
import { DeleteUserComponent } from './pages/delete-user/delete-user.component';
import { HomeComponent } from './pages/home/home.component';
import { LoginComponent } from './pages/login/login.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { StoresComponent } from './pages/stores/stores.component';

enum EMULATOR_PORTS {
  FIRESTORE = 8080,
  FUNCTIONS = 5001,
  STORAGE = 9199,
}

@NgModule({
  bootstrap: [AppComponent],
  declarations: [
    AppComponent,
    AuthLayoutComponent,
    AppLayoutComponent,
    LoginComponent,
    HomeComponent,
    NotFoundComponent,
    ConfirmModalComponent,
    AddAdminUserModalComponent,
    StoresComponent,
    EditStoreModalComponent,
    CronExpressionEditModalComponent,
    DeleteUserComponent,
    DeleteUserModalComponent,
    MediaFileManagerComponent,
    CmsComponent,
    PlatformTextPropertiesFormComponent,
    LegalContentManagerComponent,
    AddProductsStrategySelectModalComponent,
    TypedConfirmModalComponent,
  ],
  imports: [
    AngularFireModule.initializeApp(environment.firebaseConfig),
    CommonModule,
    AngularFireAnalyticsModule,
    AngularFireAuthModule,
    AngularFireStorageModule,
    AngularFirestoreModule,
    AngularFireFunctionsModule,
    AngularFireAuthGuardModule,
    FunctionsModule,
    LottieComponent,
    BrowserModule,
    RouterModule,
    AppRoutingModule,
    AgentFieldNoteComponent,
    CodemirrorModule,
    FormsModule,
    ReactiveFormsModule,
    NgbModule,
    NgSelectModule,
    ToastrModule.forRoot({
      positionClass: 'toast-top-center',
    }),
    FontAwesomeModule,
    NgbNavModule,
    Bs5UnixCronModule,
    PlatformApiModule.forRoot({
      rootUrl: environment.platformServiceUrl,
    }),
    ActionContentDirective,
    AdminUsersComponent,
    ExpandContentDirective,
    ItemActionsContentDirective,
    ItemLeadingActionsContentDirective,
    ListViewComponent,
    LoaderComponent,
  ],
  providers: [
    provideHttpClient(withInterceptors([noHttpTransferCacheInterceptor, platformAuthInterceptor])),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    providePerformance(() => getPerformance()),
    provideAnalytics(() => getAnalytics()),
    provideMessaging(() => getMessaging()),
    provideAuth(() => {
      const auth = getAuth();
      if (environment.useEmulators && !environment.dockerCompose) {
        connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
      }

      return auth;
    }),
    provideFirestore(() => {
      const firestore = getFirestore();
      if (environment.useEmulators) {
        connectFirestoreEmulator(firestore, 'localhost', EMULATOR_PORTS.FIRESTORE);
      }

      return firestore;
    }),
    provideStorage(() => {
      const storage = getStorage();
      if (environment.useEmulators && !environment.dockerCompose) {
        connectStorageEmulator(storage, 'localhost', EMULATOR_PORTS.STORAGE);
      }

      return storage;
    }),
    provideFunctions(() => {
      const functions = getFunctions();
      if (environment.useEmulators) {
        connectFunctionsEmulator(functions, 'localhost', EMULATOR_PORTS.FUNCTIONS);
      }

      return functions;
    }),
    { provide: FIREBASE_OPTIONS, useValue: environment.firebaseConfig },
    ScreenTrackingService,
    UserTrackingService,
    // PerformanceMonitoringService,
    {
      provide: FIRESTORE_SETTINGS,
      useValue: {
        experimentalAutoDetectLongPolling: !environment.featureFlags.longPolling,
        experimentalForceLongPolling: environment.featureFlags.longPolling,
        ignoreUndefinedProperties: true,
        merge: true,
      },
    },
    { provide: ANALYTICS_DEBUG_MODE, useValue: true },
    { provide: COLLECTION_ENABLED, useValue: true },
    {
      provide: USE_AUTH_EMULATOR,
      useValue:
        environment.useEmulators && !environment.dockerCompose
          ? ['http://localhost:9099']
          : undefined,
    },
    {
      provide: USE_FIRESTORE_EMULATOR,

      useValue: environment.useEmulators ? ['localhost', EMULATOR_PORTS.FIRESTORE] : undefined,
    },
    {
      provide: USE_FUNCTIONS_EMULATOR,
      useValue: environment.useEmulators ? ['localhost', EMULATOR_PORTS.FUNCTIONS] : undefined,
    },
    {
      provide: USE_STORAGE_EMULATOR,
      useValue: undefined,
    },
    { provide: USE_DEVICE_LANGUAGE, useValue: true },
    { provide: APP_VERSION, useValue: '1.0.19' },
    { provide: APP_NAME, useValue: 'Client' },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {
  constructor() {
    console.log('angular started');
  }
}
